import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ArrowLeft, ArrowRight, CheckCircle, Play, Clock, Hand, FileText } from 'lucide-react';
import type { Equipment, Project } from '../../types/project';
import {
  buildDemoScript,
  demoStateBefore,
  otherPath,
  DEMO_PATH_LABELS,
  DEMO_TAB_LABELS,
  type DemoBeat,
  type DemoContext,
  type DemoPath,
  type DemoPlan,
  type DemoSeed,
  type DemoState,
  type DemoTab,
} from './demoScript';

interface DemoOverlayProps {
  seed: DemoSeed;
  onPatchProject: (updates: Partial<Project>) => void;
  onAddEquipment: () => void;
  onUpdateEquipment: (updates: Partial<Equipment>) => void;
  onSeedAmi: () => void;
  /** Adds or updates a dwelling-inventory row, keyed by its name. */
  onAddExisting: (seed: Partial<Equipment>) => void;
  /** Opens one sub-path of the Existing Load tab, or the chooser when null. */
  onSetMode: (mode: DemoPath | null) => void;
  /**
   * Which sub-path of the Existing Load tab is open right now — the app's own
   * state. At the fork this is how the demo learns which card was clicked.
   */
  existingLoadMode: DemoPath | null;
  /** Replaces the whole sample job — used to rebuild it on every navigation. */
  onApplyState: (state: DemoState) => void;
  onNavigateTab: (tab: DemoTab) => void;
  /** Left early. */
  onExit: () => void;
  /** Watched it through. */
  onComplete: () => void;
}

type Corner = 'bl' | 'br' | 'tl' | 'tr';

interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface Mark {
  selector: string;
  tone: 'red' | 'blue';
  shape: 'oval' | 'box';
}

interface Menu {
  left: number;
  top: number;
  width: number;
  options: string[];
  active: number;
}

/**
 * The geometry for a mark drawn around an element.
 *
 * An oval has to be noticeably larger than what it encircles: a rectangle only
 * fits inside an ellipse of the same centre once its axes are scaled by about
 * root two. Two things had to be got right here, and the first attempt got each
 * of them wrong in turn — the ellipse was too small, so it sliced the corners
 * off the end buttons; and then clamping it back on screen shifted it sideways,
 * which un-centred it and clipped the same buttons again.
 *
 * So the centre is fixed, the width is whatever the screen allows, and the
 * HEIGHT is then solved from the ellipse equation so the corners are inside
 * whatever width we ended up with.
 *
 * All of which is why a long, flat target is drawn as a PILL instead, and why
 * that is decided here rather than beat by beat. Every row of controls the demo
 * circles is long and flat — the header buttons, the six tabs, the report's
 * action bar — and an ellipse cannot sit close to any of them. Round the tabs,
 * 950 x 54, it has to run 200px wider than they do and touch the edge of the
 * window just to clear their corners; there is no tighter ellipse, only a
 * taller one. A pill hugs them and still reads as circling something.
 *
 * So `oval` means "circle this", and the aspect ratio picks the geometry that
 * fits closest.
 */
const PILL_ASPECT = 3;

function markBox(rect: DOMRect, shape: 'oval' | 'box'): Box & { pill: boolean } {
  const pill = shape === 'oval' && rect.width > rect.height * PILL_ASPECT;
  if (shape === 'box' || pill) {
    const pad = pill ? 12 : 10;
    return {
      left: rect.left - pad,
      top: rect.top - pad,
      width: rect.width + pad * 2,
      height: rect.height + pad * 2,
      pill,
    };
  }
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const halfW = rect.width / 2;
  const halfH = rect.height / 2;

  // Widest half-axis that keeps the whole oval on screen, centred where it is.
  const room = Math.max(0, Math.min(cx - 6, window.innerWidth - cx - 6));
  const a = Math.min(halfW * 1.45, Math.max(room, halfW + 12));

  // Then solve (halfW/a)^2 + (halfH/b)^2 = 1 for b.
  const slack = 1 - Math.pow(halfW / a, 2);
  const b = slack > 0.04 ? Math.max(halfH / Math.sqrt(slack), halfH + 10) : halfH * 2.4;

  return { left: cx - a, top: cy - b, width: a * 2, height: b * 2, pill: false };
}

const CARD_WIDTH = 350;
const CARD_MARGIN = 20;
/** Top corners clear the app's sticky header block. */
const CARD_TOP = 92;

// Above the report viewer and every modal, since the demo drives them and the
// card has to stay reachable on top of whatever it opens.
const Z_CARD = 105;

/** Thrown to unwind a beat that is no longer the current one. */
const ABORT = Symbol('demo-aborted');

// Hard ceiling on a single beat. Whatever goes wrong — a selector that never
// resolves, a scroll that never settles — the demo does not sit there stuck.
const BEAT_CEILING_MS = 20000;

const OVERLAYS: { modal: string; close: string }[] = [
  { modal: 'validation-modal', close: 'btn-validation-cancel' },
  { modal: 'report-modal', close: 'btn-report-close' },
  { modal: 'ami-request-modal', close: 'btn-ami-request-close' },
];

/**
 * Close anything a previous beat opened, except what the incoming one expects.
 *
 * Uses each modal's own close control rather than dispatching Escape, because
 * this component listens for Escape to leave the demo — a synthetic one would
 * close the demo along with the dialog.
 */
function dismissOverlays(keep: string[] = []) {
  for (const { modal, close } of OVERLAYS) {
    if (keep.includes(modal)) continue;
    (document.querySelector(`[data-testid="${close}"]`) as HTMLElement | null)?.click();
  }
}

const prefersReducedMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

/**
 * The visible element for a selector — not merely the first in the document.
 *
 * Several of the app's screens render two layouts and let CSS pick one: the
 * equipment schedule ships a mobile card list AND a desktop table, both
 * carrying `equipment-type-0`, `equipment-amps-0` and the rest. A plain
 * querySelector returns whichever comes first in the markup, which on a desktop
 * viewport is the hidden one — a 0x0 box at the document origin. The cursor
 * then dutifully travelled to the top-left corner and sat there while the
 * fields filled themselves in from state, which is exactly what a demo must
 * never look like.
 */
function findVisible(selector: string): HTMLElement | null {
  const matches = Array.from(document.querySelectorAll<HTMLElement>(selector));
  for (const el of matches) {
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) return el;
  }
  return null;
}

/**
 * The box a mark should enclose — the union of every visible match, not just
 * the first.
 *
 * Marks are the one thing that wants this. A selector naming a container gives
 * a box as wide as the container, which for the tab strip meant a full-width
 * `<nav>`: the oval spanned the entire window and swallowed the progress bar
 * beneath it. Naming the buttons instead and unioning their boxes draws the
 * oval around what a viewer actually reads as "the tabs".
 *
 * Safe for the duplicated mobile/desktop testids too, since the layout CSS
 * hides one outright and a `display: none` element measures 0x0.
 */
function unionRect(selector: string): DOMRect | null {
  const boxes = Array.from(document.querySelectorAll<HTMLElement>(selector))
    .map((el) => el.getBoundingClientRect())
    .filter((r) => r.width > 0 && r.height > 0);
  if (!boxes.length) return null;
  const left = Math.min(...boxes.map((r) => r.left));
  const top = Math.min(...boxes.map((r) => r.top));
  const right = Math.max(...boxes.map((r) => r.right));
  const bottom = Math.max(...boxes.map((r) => r.bottom));
  return new DOMRect(left, top, right - left, bottom - top);
}

/** An ancestor that can actually be scrolled — not merely one allowed to be. */
function scrolls(el: Element): boolean {
  const overflow = getComputedStyle(el).overflowY;
  if (overflow !== 'auto' && overflow !== 'scroll' && overflow !== 'overlay') return false;
  // Tailwind's `overflow-x-auto` makes overflow-y compute to `auto` as well,
  // which would otherwise make the tab strip look scrollable in this direction.
  return el.scrollHeight > el.clientHeight + 1;
}

/**
 * Whether scrolling can bring an element into view at all.
 *
 * Pinned chrome cannot be moved by scrolling, so trying is pure stall: the
 * app's header, tab bar and progress strip live in one sticky block, and that
 * is most of what the demo points at early on.
 *
 * But "has a fixed ancestor" is NOT the same as "cannot be scrolled to", and
 * reading it that way is what stranded the authorization request. Its modal is
 * `fixed inset-0` with the dialog inside capped at 90vh and scrolling its own
 * body — so on a short screen the Send button sat below the fold, the demo
 * decided it was pinned, and the cursor slid off the bottom of the screen to
 * press something the viewer never saw. Which is exactly what a demo must not
 * do with a button marked "send".
 *
 * So the walk stops at whichever comes first: a scrollable ancestor means yes,
 * a fixed or sticky one means no.
 */
function canScrollIntoView(el: Element): boolean {
  let node: Element | null = el.parentElement;
  while (node && node !== document.body) {
    if (scrolls(node)) return true;
    const pos = getComputedStyle(node).position;
    if (pos === 'sticky' || pos === 'fixed') return false;
    node = node.parentElement;
  }
  return true;
}

/**
 * The guided demo: an oversized cursor that drives the real application on a
 * throwaway sample project, typing into real fields and clicking real controls
 * while a fixed card explains what just happened and why it matters.
 *
 * Nothing is dimmed and the card never moves. The cursor is the only thing
 * directing attention, which is what keeps the screen calm — an earlier
 * spotlight version had a lit region and a card both chasing the target, and
 * they fought each other.
 */
export function DemoOverlay({
  seed,
  onPatchProject,
  onAddEquipment,
  onUpdateEquipment,
  onSeedAmi,
  onAddExisting,
  onSetMode,
  existingLoadMode,
  onApplyState,
  onNavigateTab,
  onExit,
  onComplete,
}: DemoOverlayProps) {
  // What the viewer has chosen so far. The script is rebuilt from it whenever
  // it changes; every beat before the choice keeps its position, so changing
  // the plan never moves the viewer.
  const [plan, setPlan] = useState<DemoPlan>({ first: null, second: null });
  const beats = useMemo(() => buildDemoScript(seed, plan), [seed, plan]);
  // The step counter's denominator. Before the fork the script only runs as
  // far as the fork, and "Step 3 of 11" would read as a demo about to end —
  // so the count assumes the measured path until the viewer says otherwise,
  // then corrects itself. The paths differ by one step.
  const projectedTotal = useMemo(
    () => (plan.first ? beats.length : buildDemoScript(seed, { first: 'measured' }).length),
    [seed, plan.first, beats.length],
  );
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [interrupted, setInterrupted] = useState(false);
  const [cursor, setCursor] = useState({ x: 0, y: 0, visible: false });
  const [pressed, setPressed] = useState(false);
  const [ripple, setRipple] = useState<{ x: number; y: number; key: number } | null>(null);
  // Everything drawn around a target — rings and persistent call-outs alike.
  // Held as selectors, not coordinates, so they follow the page when it scrolls.
  const [marks, setMarks] = useState<Mark[]>([]);
  const [markBoxes, setMarkBoxes] = useState<(Box & Mark & { pill: boolean })[]>([]);
  // What the cursor is currently aimed at, so it can be re-aimed after a scroll.
  const anchorRef = useRef<{ selector: string; mode: 'on' | 'point'; fx?: number } | null>(null);
  // Stand-in for a native select, since a real one cannot be shown open.
  const [menu, setMenu] = useState<Menu | null>(null);
  // The file being dragged onto the drop zone.
  const [drop, setDrop] = useState<{ x: number; y: number; name: string; landed: boolean } | null>(null);

  // Mirrors the cursor so a click can read its position synchronously. Reading
  // it through a setCursor updater instead would mean firing the ripple as a
  // side effect inside that updater, which React is entitled to run twice.
  const cursorRef = useRef({ x: 0, y: 0 });
  // The tracking effect above runs outside makeContext, so it reaches `place`
  // through here rather than closing over a stale copy.
  const placeRef = useRef<(x: number, y: number) => void>(() => {});
  const rippleKeyRef = useRef(0);
  const runIdRef = useRef(0);
  const cardRef = useRef<HTMLDivElement>(null);
  // Which corner the card sits in. It only moves when the cursor would
  // otherwise arrive underneath it.
  const [corner, setCorner] = useState<Corner>('bl');
  const reduced = useRef(prefersReducedMotion()).current;
  const beat = beats[Math.min(index, beats.length - 1)];
  const isLast = index === beats.length - 1;

  // Hide the real pointer over the app only — the card lives outside #root, so
  // the user keeps a normal cursor for Next and Skip. Two pointers on screen at
  // once is the fastest way to make a demo feel broken.
  useEffect(() => {
    const root = document.getElementById('root');
    if (!root) return;
    root.style.cursor = playing ? 'none' : '';
    return () => {
      root.style.cursor = '';
    };
  }, [playing]);

  // A real click from the user takes the app back. Synthetic clicks the demo
  // fires itself carry isTrusted === false, so they don't trip this.
  useEffect(() => {
    function onPointerDown(e: PointerEvent) {
      if (!e.isTrusted || !playing) return;
      const card = document.getElementById('demo-card');
      if (card?.contains(e.target as Node)) return;
      runIdRef.current += 1;
      setPlaying(false);
      setInterrupted(true);
      setCursor((c) => ({ ...c, visible: false }));
    }
    window.addEventListener('pointerdown', onPointerDown, true);
    return () => window.removeEventListener('pointerdown', onPointerDown, true);
  }, [playing]);

  const makeContext = useCallback(
    (runId: number): DemoContext => {
      const alive = () => {
        if (runId !== runIdRef.current) throw ABORT;
      };
      const sleep = (ms: number) =>
        new Promise<void>((resolve, reject) => {
          const t = setTimeout(() => {
            if (runId !== runIdRef.current) reject(ABORT);
            else resolve();
          }, reduced ? Math.min(ms, 120) : ms);
          void t;
        });

      const place = (x: number, y: number) => {
        // Clamp into view. A target can be scrolled away or replaced by a
        // full-screen modal between the measurement and the move, which used to
        // send the cursor off the top of the page and leave it there.
        const cx = Math.max(4, Math.min(x, window.innerWidth - 30));
        const cy = Math.max(4, Math.min(y, window.innerHeight - 40));
        cursorRef.current = { x: cx, y: cy };
        setCursor({ x: cx, y: cy, visible: true });
      };
      placeRef.current = place;

      const fireRipple = () => {
        const { x, y } = cursorRef.current;
        setRipple({ x, y, key: ++rippleKeyRef.current });
      };

      const rectOf = async (selector: string) => {
        const el = findVisible(selector);
        if (!el) {
          // Silent misses are how the cursor ends up parked in a corner while
          // the data fills itself in. Name the selector instead.
          console.warn(`[demo] nothing visible matches ${selector}`);
          return null;
        }
        const r = el.getBoundingClientRect();
        // Wholly in view with room to spare, not merely touching the edge. A
        // button half off the bottom is one the viewer cannot see being
        // pressed, and the cursor has to travel off screen to reach it.
        const MARGIN = 16;
        const fits = r.height <= window.innerHeight - MARGIN * 2;
        const needsScroll = fits
          ? r.top < MARGIN || r.bottom > window.innerHeight - MARGIN
          : r.bottom < MARGIN || r.top > window.innerHeight - MARGIN;
        if (needsScroll && canScrollIntoView(el)) {
          el.scrollIntoView({ block: 'center', behavior: reduced ? 'auto' : 'smooth' });
          if (reduced) {
            await sleep(60);
          } else {
            // Wait for it to come to rest rather than sleeping a guess. A
            // smooth scroll is animation-driven, so how long it takes depends
            // on the distance and on what else the page is doing; the fixed
            // wait this replaces expired while the modal had moved 14 of the
            // 441 pixels it needed.
            let last = el.getBoundingClientRect().top;
            for (let i = 0, still = 0; i < 30 && still < 2; i++) {
              await sleep(70);
              const top = el.getBoundingClientRect().top;
              still = Math.abs(top - last) < 0.5 ? still + 1 : 0;
              last = top;
            }
          }
          // And if it never got there — a stalled animation, an interrupted
          // scroll — put it on screen without the animation. Better a jump
          // than a cursor that travels off screen to press something marked
          // "send" where nobody can see it happen.
          const settled = el.getBoundingClientRect();
          if (settled.top < 0 || settled.bottom > window.innerHeight) {
            el.scrollIntoView({ block: 'center', behavior: 'auto' });
            await sleep(reduced ? 30 : 140);
          }
        }
        return { el, rect: el.getBoundingClientRect() };
      };

      /**
       * Parks the cursor just off an element's edge instead of on top of it.
       * The arrow's tip is its top-left corner, so sitting past the right edge
       * leaves it aimed back at the target — which matters when the target is a
       * number the user is meant to read.
       */
      const pointAt = async (selector: string) => {
        alive();
        const found = await rectOf(selector);
        if (!found) return null;
        const { rect } = found;
        anchorRef.current = { selector, mode: 'point' };
        const room = window.innerWidth - rect.right > 46;
        place(room ? rect.right + 6 : rect.left - 28, rect.top + rect.height * 0.55);
        await sleep(reduced ? 60 : 620);
        return found;
      };

      const moveTo: DemoContext['moveTo'] = async (selector, offset) => {
        alive();
        const found = await rectOf(selector);
        if (!found) return;
        const { rect } = found;
        // offset.x as a 0..1 fraction picks a point along a wide target.
        const fx = offset?.x ?? 0;
        const x = fx > 0 && fx <= 1 ? rect.left + rect.width * fx : rect.left + Math.min(26, rect.width / 2);
        const y = rect.top + (offset?.y ?? rect.height / 2);
        anchorRef.current = { selector, mode: 'on', fx };
        place(x, y);
        await sleep(reduced ? 60 : 620);
      };

      const press = async () => {
        setPressed(true);
        fireRipple();
        await sleep(130);
        setPressed(false);
      };

      const click: DemoContext['click'] = async (selector, opts) => {
        await moveTo(selector, opts);
        alive();
        await press();
        if (opts?.real) findVisible(selector)?.click();
        await sleep(180);
      };

      return {
        moveTo,
        click,
        sleep,
        async pulse(selector) {
          alive();
          const found = await rectOf(selector);
          if (!found) return;
          found.el.classList.remove('demo-pulse');
          void found.el.offsetWidth;
          found.el.classList.add('demo-pulse');
          await sleep(reduced ? 60 : 700);
          found.el.classList.remove('demo-pulse');
        },
        async ring(selector) {
          // Cursor points at it from outside, and the ring is drawn around it —
          // between them nothing covers the value being read out.
          const found = await pointAt(selector);
          if (!found) return;
          setMarks((m) => [...m, { selector, tone: 'blue', shape: 'box' }]);
          await sleep(reduced ? 120 : 1600);
          setMarks((m) => m.filter((x) => x.selector !== selector));
        },
        async mark(selector: string, opts?: { tone?: 'red' | 'blue'; shape?: 'oval' | 'box' }) {
          const found = await pointAt(selector);
          if (!found) return;
          setMarks((m) =>
            m.some((x) => x.selector === selector)
              ? m
              : [...m, { selector, tone: opts?.tone ?? 'red', shape: opts?.shape ?? 'oval' }],
          );
          await sleep(reduced ? 120 : 700);
        },
        async chooseOption(selector, label, apply) {
          alive();
          const found = await rectOf(selector);
          if (!found) return;
          const select = found.el as HTMLSelectElement;

          await moveTo(selector, { x: 0.9 });
          await press();

          // The utility picker is a combobox, not a <select>: its list exists
          // only while it is open, so open it for real and read the rows it
          // renders. Every other caller here is still a <select>.
          const isSelect = typeof select.options?.length === 'number';
          let liveRows: HTMLElement[] = [];
          if (!isSelect) {
            (found.el as HTMLElement).focus();
            await sleep(reduced ? 60 : 260);
            liveRows = Array.from(
              document.querySelectorAll<HTMLElement>('#utility-listbox [role="option"]')
            );
          }
          const options = isSelect
            ? Array.from(select.options)
            : liveRows.map((el) => ({ textContent: el.textContent, value: '' } as HTMLOptionElement));
          const target = options.findIndex((o) => (o.textContent ?? '').trim() === label);

          const rect = select.getBoundingClientRect();
          setMenu({
            left: rect.left,
            top: rect.bottom + 4,
            width: Math.max(rect.width, 180),
            options: options.map((o) => (o.textContent ?? '').trim()),
            active: -1,
          });
          await sleep(reduced ? 120 : 700);

          // Walk down to it so the choice is something the eye can follow.
          const stop = target < 0 ? options.length - 1 : target;
          for (let i = 0; i <= stop; i++) {
            alive();
            setMenu((m) => (m ? { ...m, active: i } : m));
            const row = document.querySelector(`[data-demo-option="${i}"]`);
            if (row) {
              const r = row.getBoundingClientRect();
              place(r.left + 22, r.top + r.height / 2);
            }
            await sleep(reduced ? 40 : i === stop ? 420 : 190);
          }

          await press();
          await sleep(reduced ? 60 : 260);
          setMenu(null);
          if (isSelect) {
            if (target >= 0) apply(options[target].value);
          } else if (target >= 0 && liveRows[target]) {
            // A real click on the real row, so the component's own handler runs
            // and the demo is showing the thing it claims to show.
            liveRows[target].click();
          } else {
            // The row was not there to click — a ZIP with no coverage, say.
            // Patch anyway so the narration does not stall on a dead step.
            apply('');
          }
          await sleep(reduced ? 60 : 320);
        },
        async dropFile(selector, fileName) {
          alive();
          const found = await rectOf(selector);
          if (!found) return;
          const rect = found.el.getBoundingClientRect();
          // Comes in from the left, as a file dragged off a desktop would.
          setDrop({ x: Math.max(40, rect.left - 220), y: rect.top - 60, name: fileName, landed: false });
          place(Math.max(40, rect.left - 220) + 30, rect.top - 46);
          await sleep(reduced ? 80 : 520);
          const cx = rect.left + rect.width / 2 - 90;
          const cy = rect.top + rect.height / 2 - 18;
          setDrop((d) => (d ? { ...d, x: cx, y: cy } : d));
          place(cx + 30, cy + 14);
          await sleep(reduced ? 100 : 900);
          setDrop((d) => (d ? { ...d, landed: true } : d));
          await press();
          await sleep(reduced ? 80 : 420);
          setDrop(null);
        },
        async type(selector, text, apply) {
          await moveTo(selector);
          alive();
          await press();
          const el = findVisible(selector);
          el?.focus?.();
          if (reduced) {
            apply(text);
            await sleep(120);
            return;
          }
          for (let i = 1; i <= text.length; i++) {
            alive();
            apply(text.slice(0, i));
            await sleep(30 + Math.round(24 * Math.abs(Math.sin(i))));
          }
          await sleep(320);
          el?.blur?.();
        },
        async waitFor(selector, timeoutMs = 2000) {
          const started = performance.now();
          while (performance.now() - started < timeoutMs) {
            alive();
            if (findVisible(selector)) return true;
            await sleep(90);
          }
          return false;
        },
        patch: onPatchProject,
        setTab: onNavigateTab,
        seedAmi: onSeedAmi,
        addEquipment: onAddEquipment,
        updateEquipment: onUpdateEquipment,
        addExisting: onAddExisting,
        setMode: onSetMode,
      };
    },
    [onPatchProject, onNavigateTab, onSeedAmi, onAddEquipment, onUpdateEquipment, onAddExisting, onSetMode, reduced],
  );

  // Play the current beat. Cancels cleanly if the user moves on mid-scene.
  const playBeat = useCallback(
    (target: DemoBeat) => {
      const runId = ++runIdRef.current;
      setPlaying(true);
      setInterrupted(false);
      onNavigateTab(target.tab);

      // Watchdog: whatever happens, this beat stops blocking Next.
      const ceiling = setTimeout(() => {
        if (runId === runIdRef.current) {
          console.warn(`[demo] beat "${target.id}" hit the ${BEAT_CEILING_MS}ms ceiling`);
          setPlaying(false);
        }
      }, BEAT_CEILING_MS);

      const settle = () => {
        clearTimeout(ceiling);
        // Only the run that is still current owns the flag. An aborted run
        // whose successor is already playing must not clear it.
        if (runId === runIdRef.current) setPlaying(false);
      };

      const ctx = makeContext(runId);
      target
        .run(ctx)
        .then(settle)
        .catch((err) => {
          if (err !== ABORT) console.error('[demo] beat failed:', target.id, err);
          settle();
        });
    },
    [makeContext, onNavigateTab],
  );

  // Entering any beat — forwards or back — rebuilds the sample job from scratch
  // to the state it should be in when that scene starts, then plays it.
  //
  // This is why Back genuinely restarts a beat with blank fields, and why a
  // second visit to the equipment beat does not leave two heat pumps behind:
  // nothing is ever undone, the job is simply replayed. It also covers the user
  // who presses Next mid-sentence, since the skipped beat's `settle` is part of
  // the replay for the next one.
  useEffect(() => {
    dismissOverlays(beat.keeps);
    setMarks([]);
    anchorRef.current = null;
    setMenu(null);
    setDrop(null);
    onApplyState(demoStateBefore(beats, index));
    playBeat(beat);
    return () => {
      runIdRef.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  // The fork. The viewer clicks one of the app's real Existing Load cards, the
  // app opens that path, and the mode prop changes — that change is the
  // choice. The value the beat was ENTERED with is ignored: arriving by Back
  // from a path, the prop still reads that path for a render until the
  // rebuild clears it, and acting on it would skip the fork entirely.
  const awaitingPath = beat.awaits === 'path';
  const forkEntryModeRef = useRef<DemoPath | null | undefined>(undefined);
  const choosePath = useCallback((path: DemoPath) => {
    setPlan({ first: path, second: null });
    setIndex((i) => i + 1);
  }, []);
  useEffect(() => {
    if (!awaitingPath) {
      forkEntryModeRef.current = undefined;
      return;
    }
    if (forkEntryModeRef.current === undefined) {
      forkEntryModeRef.current = existingLoadMode;
      return;
    }
    if (existingLoadMode && existingLoadMode !== forkEntryModeRef.current) {
      choosePath(existingLoadMode);
    } else {
      forkEntryModeRef.current = existingLoadMode;
    }
  }, [awaitingPath, existingLoadMode, choosePath]);

  /**
   * The card's own way to choose, for a keyboard or a screen where the rings
   * are awkward to reach. It presses the real card, so the app's own handler
   * runs and the effect above hears about it — the same as a click.
   */
  const pickPath = (path: DemoPath) => {
    const card = findVisible(`[data-testid="card-${path}"]`);
    if (card) card.click();
    else choosePath(path);
  };

  // The offer. Taking it appends the other path before the ending; declining
  // it drops any second path a previous visit added. Both move on one beat.
  const takeOffer = () => {
    setPlan((p) => ({ first: p.first, second: p.first ? otherPath(p.first) : null }));
    setIndex((i) => i + 1);
  };
  const declineOffer = () => {
    setPlan((p) => ({ first: p.first, second: null }));
    setIndex((i) => i + 1);
  };

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onExit();
        return;
      }
      if (e.key !== 'Enter' && e.key !== ' ') return;
      // Let a focused control handle its own key — otherwise pressing Enter on
      // the Next button would advance twice.
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'BUTTON' || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      e.preventDefault();
      goNextRef.current();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onExit]);

  // Everything the demo draws is anchored to an element, not to a coordinate,
  // and re-measured on a poll as well as on scroll and resize.
  //
  // This is what makes the demo survive the page moving under it. A scroll —
  // the user's, or the app's own when a tab changes height — used to leave the
  // cursor pointing at empty space and the rings floating over the wrong rows,
  // because both were placed once from a rect measured at that instant.
  useEffect(() => {
    const measure = () => {
      setMarkBoxes(
        marks
          .map((m) => {
            const rect = unionRect(m.selector);
            if (!rect) return null;
            // markBox already sizes itself to the room available, so the box
            // must NOT be nudged afterwards — that is what un-centres it.
            return { ...m, ...markBox(rect, m.shape) };
          })
          .filter(Boolean) as (Box & Mark & { pill: boolean })[],
      );

      const anchor = anchorRef.current;
      if (!anchor) return;
      const el = findVisible(anchor.selector);
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (anchor.mode === 'point') {
        const room = window.innerWidth - r.right > 46;
        placeRef.current(room ? r.right + 6 : r.left - 28, r.top + r.height * 0.55);
      } else {
        const fx = anchor.fx ?? 0;
        placeRef.current(
          fx > 0 && fx <= 1 ? r.left + r.width * fx : r.left + Math.min(26, r.width / 2),
          r.top + r.height / 2,
        );
      }
    };
    measure();
    const poll = setInterval(measure, 200);
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      clearInterval(poll);
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [marks]);

  // Get out of the way. The card only relocates when the cursor is about to
  // arrive underneath it, and then goes to the opposite quadrant — so it moves
  // rarely, and never to somewhere the action is heading.
  useEffect(() => {
    if (!cursor.visible || !cardRef.current) return;
    const r = cardRef.current.getBoundingClientRect();
    const pad = 56;
    const collides =
      cursor.x > r.left - pad &&
      cursor.x < r.right + pad &&
      cursor.y > r.top - pad &&
      cursor.y < r.bottom + pad;
    if (!collides) return;
    const vertical = cursor.y > window.innerHeight / 2 ? 't' : 'b';
    const horizontal = cursor.x < window.innerWidth / 2 ? 'r' : 'l';
    setCorner(`${vertical}${horizontal}` as Corner);
  }, [cursor.x, cursor.y, cursor.visible]);

  const cardHeight = cardRef.current?.offsetHeight ?? 250;
  const cardLeft =
    corner === 'bl' || corner === 'tl'
      ? CARD_MARGIN
      : window.innerWidth - CARD_WIDTH - CARD_MARGIN;
  const forcedTop = beat.card === 'top';
  const cardTop =
    forcedTop || corner === 'tl' || corner === 'tr'
      ? CARD_TOP
      : window.innerHeight - cardHeight - CARD_MARGIN;

  // A beat that waits on the viewer's choice does not move on Next or Enter.
  const goNext = () => {
    if (beat.awaits) return;
    if (isLast) onComplete();
    else setIndex((i) => i + 1);
  };
  // The key handler is registered once; route through a ref so it always calls
  // the current one rather than the first render's.
  const goNextRef = useRef(goNext);
  goNextRef.current = goNext;
  const goBack = () => setIndex((i) => Math.max(0, i - 1));
  const percent = Math.round(((index + 1) / projectedTotal) * 100);

  return createPortal(
    <div data-testid="demo-overlay">
      {markBoxes.map((m) => (
        <div
          key={m.selector}
          data-testid={m.shape === 'oval' ? 'demo-callout' : 'demo-ring'}
          className={
            m.shape === 'box'
              ? 'demo-ring-box'
              : m.pill
              ? 'demo-callout demo-callout-pill'
              : 'demo-callout'
          }
          style={{
            position: 'fixed',
            left: m.left,
            top: m.top,
            width: m.width,
            height: m.height,
            borderColor: m.tone === 'red' ? '#dc2626' : '#2563eb',
            zIndex: Z_CARD - 2,
            pointerEvents: 'none',
          }}
        />
      ))}

      {menu && (
        <div
          data-testid="demo-menu"
          style={{
            position: 'fixed',
            left: menu.left,
            top: menu.top,
            width: menu.width,
            maxHeight: 300,
            overflow: 'hidden',
            zIndex: Z_CARD - 1,
            pointerEvents: 'none',
          }}
          className="bg-white rounded-lg shadow-2xl border border-gray-300 py-1"
        >
          {menu.options.map((label, i) => (
            <div
              key={label + i}
              data-demo-option={i}
              className={`px-3 py-1.5 text-sm ${
                i === menu.active ? 'bg-blue-600 text-white' : 'text-gray-800'
              }`}
            >
              {label}
            </div>
          ))}
        </div>
      )}

      {drop && (
        <div
          data-testid="demo-file"
          style={{
            position: 'fixed',
            left: drop.x,
            top: drop.y,
            zIndex: Z_CARD - 1,
            pointerEvents: 'none',
            transition: reduced ? 'none' : 'left 850ms cubic-bezier(.3,.02,.2,1), top 850ms cubic-bezier(.3,.02,.2,1)',
            transform: drop.landed ? 'scale(0.9)' : 'scale(1)',
            opacity: drop.landed ? 0 : 1,
          }}
          className="flex items-center gap-2 rounded-lg bg-white border border-gray-300 shadow-xl px-3 py-2"
        >
          <FileText size={16} className="text-blue-600 flex-shrink-0" />
          <span className="text-xs font-medium text-gray-800 whitespace-nowrap">{drop.name}</span>
        </div>
      )}

      {cursor.visible && (
        <>
          <svg
            data-testid="demo-cursor"
            viewBox="0 0 14 21"
            aria-hidden="true"
            style={{
              position: 'fixed',
              left: cursor.x,
              top: cursor.y,
              width: 26,
              height: 39,
              zIndex: Z_CARD + 1,
              pointerEvents: 'none',
              filter: 'drop-shadow(0 3px 5px rgba(15,23,42,0.35))',
              transform: pressed ? 'scale(0.82)' : 'scale(1)',
              transformOrigin: '0 0',
              transition: reduced
                ? 'none'
                : 'left 620ms cubic-bezier(.35,.02,.2,1), top 620ms cubic-bezier(.35,.02,.2,1), transform 120ms ease',
            }}
          >
            <path
              d="M0.6 0.6 L0.6 16.4 L4.6 12.7 L7.1 18.4 L9.4 17.4 L6.9 11.8 L12.2 11.8 Z"
              fill="#111827"
              stroke="#ffffff"
              strokeWidth="1.1"
              strokeLinejoin="round"
            />
          </svg>
          {ripple && (
            <span
              key={ripple.key}
              className="demo-ripple"
              style={{ position: 'fixed', left: ripple.x, top: ripple.y, zIndex: Z_CARD }}
            />
          )}
        </>
      )}

      <div
        id="demo-card"
        data-testid="demo-card"
        ref={cardRef}
        style={{
          position: 'fixed',
          left: cardLeft,
          top: cardTop,
          width: CARD_WIDTH,
          zIndex: Z_CARD,
          transition: reduced ? 'none' : 'left 320ms cubic-bezier(.4,0,.2,1), top 320ms cubic-bezier(.4,0,.2,1)',
        }}
        className="bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200"
      >
        <div className="h-1 bg-gray-100">
          <div
            className="h-1 bg-blue-600 transition-all duration-300"
            style={{ width: `${percent}%` }}
          />
        </div>

        <div className="p-4">
          <div className="flex items-start justify-between gap-3">
            <p
              data-testid="demo-step-label"
              className="text-[11px] font-semibold uppercase tracking-wide text-blue-600 mb-0.5"
            >
              Step {index + 1} of {projectedTotal} · {DEMO_TAB_LABELS[beat.tab]}
            </p>
            <button
              type="button"
              onClick={onExit}
              data-testid="btn-demo-exit"
              aria-label="Exit demo"
              className="text-gray-400 hover:text-gray-600 flex-shrink-0"
            >
              <X size={18} />
            </button>
          </div>

          <h3 data-testid="demo-title" className="text-base font-semibold text-gray-900">
            {beat.title}
          </h3>
          <p className="text-sm text-gray-600 leading-relaxed mt-1.5">{beat.body}</p>

          {beat.invite && (
            <p
              data-testid="demo-invite"
              className="mt-2.5 flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-900"
            >
              <Hand size={14} className="flex-shrink-0 mt-0.5" />
              <span>
                <span className="font-semibold">Try it yourself: </span>
                {beat.invite}
              </span>
            </p>
          )}

          {beat.aside && (
            <p
              data-testid="demo-aside"
              className="mt-2.5 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900"
            >
              <Clock size={14} className="flex-shrink-0 mt-0.5" />
              {beat.aside}
            </p>
          )}

          {interrupted && (
            <div
              data-testid="demo-interrupted"
              className="mt-3 flex items-center justify-between gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2"
            >
              <span className="text-xs text-amber-900">
                Paused while you have a look — carry on whenever.
              </span>
              <button
                type="button"
                onClick={() => playBeat(beat)}
                data-testid="btn-demo-resume"
                className="inline-flex items-center gap-1 text-xs font-semibold text-amber-900 hover:text-amber-700 flex-shrink-0"
              >
                <Play size={12} />
                Replay
              </button>
            </div>
          )}

          {beat.awaits === 'path' && (
            <p
              data-testid="demo-choice-hint"
              className="mt-3 text-xs text-gray-500 text-center"
            >
              Click one of the two cards on screen to carry on — or pick here.
            </p>
          )}

          <div className="flex items-center justify-between mt-4 gap-2">
            <button
              type="button"
              onClick={goBack}
              disabled={index === 0}
              data-testid="btn-demo-back"
              className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors flex-shrink-0 ${
                index === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ArrowLeft size={15} />
              Back
            </button>

            {beat.awaits === 'path' ? (
              // The fork: Next is gone. The viewer chooses on screen, or here.
              <div className="flex items-center gap-2">
                {(['measured', 'traditional'] as DemoPath[]).map((path) => (
                  <button
                    key={path}
                    type="button"
                    onClick={() => pickPath(path)}
                    data-testid={`btn-demo-pick-${path}`}
                    className="px-3 py-2 text-xs font-semibold text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    {DEMO_PATH_LABELS[path]}
                  </button>
                ))}
              </div>
            ) : beat.awaits === 'offer' && plan.first ? (
              // The offer: the other method on the same house, or the report.
              <div className="flex flex-col items-end gap-1.5">
                <button
                  type="button"
                  onClick={takeOffer}
                  data-testid="btn-demo-offer-other"
                  className="flex items-center gap-1 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Show me {DEMO_PATH_LABELS[otherPath(plan.first)].toLowerCase()} too
                  <ArrowRight size={15} />
                </button>
                <button
                  type="button"
                  onClick={declineOffer}
                  data-testid="btn-demo-offer-report"
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  On to the report
                  <ArrowRight size={14} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={goNext}
                data-testid={isLast ? 'btn-demo-finish' : 'btn-demo-next'}
                className="flex items-center gap-1 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
              >
                {isLast ? (
                  <>
                    <CheckCircle size={15} />
                    Start my first project
                  </>
                ) : (
                  <>
                    Next
                    <ArrowRight size={15} />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
